import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { Company } from 'src/app/core/models/company';
import { CompanyService } from 'src/app/core/services/company.service';
import { MARKET_PLACE_IDS } from '../../../../../core/constants/asc.constant';
import { MODAL_DATA } from '@stockaid/services';
import { SnotifyService } from 'ng-snotify';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SpOService } from 'src/app/core/services/spo.service';
import { PopupService, OAuthResult } from 'src/app/core/services/utils/popup.service';
import { switchMap, takeUntil } from 'rxjs/operators';
import { CompanySelectionService } from 'src/app/core/services/company-selection.service';
import { COMMON_FORMAT_DATE, FORMAT_PATTERN } from 'src/app/core/constants/format-date.constant';
import { subtractByYears } from '@stockaid/utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-update-spo-company-modal',
  templateUrl: './update-spo-company-modal.component.html',
  styleUrls: ['./update-spo-company-modal.component.scss'],
})
export class UpdateSPOCompanyModalComponent implements OnInit, OnDestroy {
  companyForm: FormGroup;
  savingCompany: boolean;
  cancelling: boolean;
  message: string;
  marketplaceIds = MARKET_PLACE_IDS;
  ascInitialDateRange: any = {};
  private unsubscribe: Subject<boolean> = new Subject<boolean>();
  readonly FORMAT_PATTERN = FORMAT_PATTERN;
  readonly FORMAT_DATE = COMMON_FORMAT_DATE;
  readonly showRoutineSecurityMessage = environment.showRoutineSecurityMessage;

  constructor(
    @Inject(MODAL_DATA) public data: any,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private companyService: CompanyService,
    private snotifyService: SnotifyService,
    private companySelectionService: CompanySelectionService,
    private ascoService: SpOService,
    private popupService: PopupService
  ) {
    this.companyForm = this.fb.group({
      companyType: [''],
      companyInfo: this.fb.group({}),
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  ngOnInit(): void {
    const min = new Date();
    min.setFullYear(new Date().getFullYear() - 4);
    this.ascInitialDateRange = {
      min,
      max: new Date(),
    };

    this.buildCompanyForm(this.data.company);
    this.message = this.data.message || '';
  }

  get companyType(): FormControl {
    return this.companyForm.controls['companyType'] as FormControl;
  }

  get companyInfo(): FormGroup {
    return this.companyForm.controls['companyInfo'] as FormGroup;
  }

  get companyName(): FormControl {
    return this.companyForm.controls['companyInfo'].get(
      'companyName'
    ) as FormControl;
  }

  get currentCompany(): Company {
    return this.companyService.currentCompany();
  }

  private buildCompanyForm(company?: Company) {
    this.message = '';
    this.companyForm = this.fb.group({
      companyType: [''],
      companyInfo: this.fb.group({}),
    });

    this.companyInfo.setControl(
      'companyName',
      this.fb.control(company?.companyName || '', Validators.required)
    );
    this.companyInfo.setControl(
      'marketplaceId',
      this.fb.control(
        this.marketplaceIds.find((m) => m.val === company?.marketplaceId),
        Validators.required
      )
    );

    this.companyInfo.setControl(
      'initialSyncDate',
      this.fb.control(
        { value: subtractByYears(new Date(), 4), disabled: environment.production },
        Validators.required
      )
    );
  }

  cancel(company: Company): void {
    if (this.cancelling) {
      return;
    }

    if (company.lastSyncDate) {
      this.activeModal.dismiss();
      return;
    }

    // Delete the new company that hasn't been authorized, and wait for it to
    // finish before dismissing. Dismissing first let a fast re-submit on the
    // page behind this modal race the still-in-flight DELETE and hit a stale
    // "company already exists" check server-side.
    this.cancelling = true;
    this.companyService
      .delete(company.companyKey)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this.companySelectionService.removeCompany(company);
          this.cancelling = false;
          this.activeModal.dismiss();
        },
        () => {
          this.cancelling = false;
          this.activeModal.dismiss();
        }
      );
  }

  updateSPOCompany(): void {
    if (this.companyForm.invalid || this.savingCompany) {
      return;
    }

    const companyData = this.buildCompanyData();
    this.savingCompany = true;
    this.message = '';

    if (companyData.sellerId) {
      this.reauthorizeExistingCompany(companyData);
    } else {
      this.reauthorizeNewCompany(companyData);
    }
  }

  private buildCompanyData(): Company {
    const companyName = this.companyForm.controls['companyInfo'].get('companyName')?.value;
    const initialSyncDate = this.companyForm.controls['companyInfo'].get('initialSyncDate')?.value;
    const marketplaceId = this.companyForm.controls['companyInfo'].get('marketplaceId')?.value?.val;
    const startDate = new Date(initialSyncDate);
    startDate.setHours(0, 0, 0, 0);

    return {
      ...this.data.company,
      companyName,
      initialSyncDate: startDate.toISOString(),
      marketplaceId,
    };
  }

  private reauthorizeExistingCompany(companyData: Company): void {
    this.ascoService
      .getAuthUri(companyData.marketplaceId, companyData.companyKey)
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((uri) => this.popupService.openASCOPopup(uri))
      )
      .subscribe(
        (result) => this.handleOAuthResult(result, 'Re-connected to Amazon successfully.'),
        (error) => this.handleOAuthError(error)
      );
  }

  private reauthorizeNewCompany(companyData: Company): void {
    this.companyService
      .updateCompanyInfo(companyData)
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((res) => this.ascoService.getAuthUri(res.marketplaceId, res.companyKey)),
        switchMap((uri) => this.popupService.openASCOPopup(uri))
      )
      .subscribe(
        (result) => this.handleOAuthResult(result, 'Save Company Info successfully.'),
        (error) => this.handleOAuthError(error)
      );
  }

  private handleOAuthResult(result: OAuthResult, successMessage: string): void {
    this.savingCompany = false;
    if (result?.success) {
      this.snotifyService.success(successMessage);
      this.activeModal.close(true);
    } else {
      this.message = result?.error || 'Failed to authorize Amazon account.';
      this.snotifyService.error(this.message);
    }
  }

  private handleOAuthError(error: any): void {
    this.savingCompany = false;
    this.message = error?.message || error?.statusText || 'An unexpected error occurred.';
    this.snotifyService.error(this.message);
  }
}
